import figlet from "figlet";

/** Fonts offered (figlet, MIT). Each is loaded on demand so the page stays small. */
export const ASCII_FONTS: Record<string, () => Promise<{ default: string }>> = {
  Standard: () => import("figlet/importable-fonts/Standard.js"),
  Big: () => import("figlet/importable-fonts/Big.js"),
  Slant: () => import("figlet/importable-fonts/Slant.js"),
  Small: () => import("figlet/importable-fonts/Small.js"),
  Mini: () => import("figlet/importable-fonts/Mini.js"),
  Banner3: () => import("figlet/importable-fonts/Banner3.js"),
  Block: () => import("figlet/importable-fonts/Block.js"),
  "ANSI Shadow": () => import("figlet/importable-fonts/ANSI Shadow.js"),
  Doom: () => import("figlet/importable-fonts/Doom.js"),
  Graffiti: () => import("figlet/importable-fonts/Graffiti.js"),
  Ghost: () => import("figlet/importable-fonts/Ghost.js"),
  Bubble: () => import("figlet/importable-fonts/Bubble.js"),
};

const loaded = new Set<string>();

/** True when every character is plain ASCII (figlet fonts have no Hindi, emoji, etc.). */
export const isAscii = (s: string) => /^[\x00-\x7f]*$/.test(s);

const EXTRA: Record<string, string> = { "ß": "ss", "æ": "ae", "Æ": "AE", "œ": "oe", "Œ": "OE", "ø": "o", "Ø": "O", "đ": "d", "Đ": "D", "ł": "l", "Ł": "L", "ð": "d", "Ð": "D", "þ": "th", "Þ": "TH", "ı": "i", "‘": "'", "’": "'", "“": '"', "”": '"', "–": "-", "—": "-", "…": "..." };

/** Turns accented Latin letters and typographic quotes/dashes into plain ASCII (é to e, ñ to n, ß to ss). Anything else is kept. */
export function transliterate(s: string): string {
  return s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^\x00-\x7f]/g, (c) => EXTRA[c] ?? c).normalize("NFC");
}

export async function renderBanner(text: string, font: string, align: "left" | "center" = "left"): Promise<string> {
  const load = ASCII_FONTS[font];
  if (!load) throw new Error(`Unknown font ${font}`);
  if (!loaded.has(font)) {
    const mod = await load();
    figlet.parseFont(font, mod.default);
    loaded.add(font);
  }
  const blocks = text.split("\n").map((line) => (line.trim() ? figlet.textSync(line, { font: font as never }).replace(/\s+$/gm, "") : ""));
  let out = blocks.join("\n\n").replace(/^\n+|\n+$/g, "");
  if (align === "center") {
    const lines = out.split("\n");
    const w = Math.max(...lines.map((l) => l.length));
    out = lines.map((l) => " ".repeat(Math.floor((w - l.length) / 2)) + l).join("\n");
  }
  return out;
}

export const widestLine = (art: string) => Math.max(0, ...art.split("\n").map((l) => l.length));
